import { useState } from 'react';

import { Box, Button, Divider, Grid, styled, Typography } from '@mui/material';
import { Formik } from 'formik';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

import { CustomInput } from 'src/components/CustomInput';
import { DropDown } from 'src/components/FormikComponents';
import Loader from 'src/components/Loader/Loader';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import {
    ADD_USER_ROLE_INITIAL_VALUES,
    DEFAULT_BORDER,
    HIERARCHIES,
    Hierarchy,
} from 'src/constants';
import { CREATE_USER_ROLE_MUTATION } from 'src/graphql/mutations/userRoles.mutation';
import { GET_BUSINESSES } from 'src/graphql/queries/businesses.query';
import { GET_DETAILED_MERCHANT_BY_ID } from 'src/graphql/queries/merchants.query';
import { GET_PARTNER_OF_PARTNER } from 'src/graphql/queries/subpartners.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { addUserRoleValidationSchema } from 'src/utils/validations';

import PartnerFeatures from './PartnerFeatures';

const Form = styled('form')({
    width: '100%',
});

const initialPermissions = {
    addPartner: false,
    viewPartner: false,
    editPartner: false,
    changePartnerStatus: false,
    addMerchant: false,
    addService: false,
    viewMerchant: false,
    editMerchant: false,
    cancelServices: false,
    changeMerchantStatus: false,
    unmaskPll: false,
    leadManagement: false,
    pricingAndProductManagement: false,
    reportingExcludingFinance: false,
    reportingIncludingFinance: false,
    stockManagement: false,
    viewFinance: false,
    viewCommissions: false,
    viewSelfBillingInvoices: false,
    addInvoice: false,
    addPayment: false,
    addCredit: false,
    partnerShop: false,
    serviceDesk: false,
    configurationUserManagement: false,
    configurationAuditLog: false,
    configurationFinance: false,
    configurationSage: false,
    managePartnerResources: false,
    configurationPartnerResources: false,
    configurationOther: false,
    customerSuccess: false,
    deleteApplication: false,
    accountStatements: false,
};

const AddUserRolePage = () => {
    const [partnerDropDownOptions, setPartnerDropDownOptions] = useState();
    const [permissions, setPermissions] = useState(initialPermissions);
    const [partnerOfPartnerDropDownOptions, setPartnerOfPartnerDropDownOptions] = useState([]);
    const [partnerLegalName, setPartnerLegalName] = useState('');
    const [partnerOfPartnerId, setPartnerOfPartnerId] = useState(null);
    const [merchantDropDownOptions, setMerchantDropDownOptions] = useState([]);
    const [selectedMerchant, setSelectedMerchant] = useState('');
    const [merchantId, setMerchantId] = useState(null);

    // queries and mutations
    const { data: organisationData, loading: loadingOrganisationData } = useQuery(GET_BUSINESSES, {
        onError: () => {
            showErrorToast('Error fetching organisation list');
        },
    });

    const [createRoleMutation] = useMutation(CREATE_USER_ROLE_MUTATION, {
        onCompleted: () => {
            showSuccessToast('User Role Created Successfully');
            navigate('/user-management/user-roles');
        },
        onError: () => {
            showErrorToast('Error Creating User Role');
        },
    });

    const handleHierarchyDropDownChange = (selectedValue) => {
        setPartnerDropDownOptions(
            organisationData?.businesses.filter((business) => {
                return business.hierarchyLevel === selectedValue.replace(/\s+/g, '_').toUpperCase();
            })
        );
        if (selectedValue === Hierarchy.PARTNER_OF_PARTNER) {
            const partnerOfPartnerBusinesses = organisationData?.businesses.filter((business) => {
                return business.hierarchyLevel === Hierarchy.PARTNER_OF_PARTNER;
            });
            setPartnerOfPartnerDropDownOptions(partnerOfPartnerBusinesses);
        }

        if (selectedValue === Hierarchy.PARTNER) {
            const partnerBusinesses = organisationData?.businesses.filter((business) => {
                return business.hierarchyLevel === Hierarchy.PARTNER;
            });
            setPartnerDropDownOptions(partnerBusinesses);
        }

        if (selectedValue === Hierarchy.MERCHANT) {
            const merchantBusinesses = organisationData?.businesses.filter((business) => {
                return business.hierarchyLevel === Hierarchy.MERCHANT;
            });
            setMerchantDropDownOptions(merchantBusinesses);
        }
    };

    const { data: partnerOfPartnerQueryData } = useQuery(GET_PARTNER_OF_PARTNER, {
        variables: { id: partnerOfPartnerId },
        skip: !partnerOfPartnerId,
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const fetchedLegalName = data.partnerOfPartner.partner.business.legalName;
            setPartnerLegalName(fetchedLegalName);
        },
    });

    const { data: merchantQueryData } = useQuery(GET_DETAILED_MERCHANT_BY_ID, {
        variables: { id: merchantId },
        skip: !merchantId,
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const fetchedPartnerlName = data.getMerchantById.partnerName;
            setSelectedMerchant(fetchedPartnerlName);
        },
    });

    const initialValues = {
        ...ADD_USER_ROLE_INITIAL_VALUES,
        permissions,
    };

    const handlePermissionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPermissions({
            ...permissions,
            [event.target.name]: event.target.checked,
        });
    };

    const handleCreateNewUserRole = async (values) => {
        const inputValues = {
            name: values.name,
            hierarchyLevel: values.hierarchyLevel,
            permissions,
        };
        await createRoleMutation({
            variables: {
                input: inputValues,
            },
        });
        setPermissions(initialPermissions);
    };

    return (
        <PageContent
            heading="Add User Role"
            subHeading={`Create a new user role and assign the correct permissions for this role's permissions and platform access`}
        >
            <MetaTags title="AddUserRole" description="AddUserRole page" />
            {loadingOrganisationData && <Loader />}
            <Grid container>
                <Formik
                    initialValues={initialValues}
                    validationSchema={addUserRoleValidationSchema}
                    onSubmit={async (values) => {
                        handleCreateNewUserRole(values);
                    }}
                >
                    {({
                        errors,
                        isValid,
                        dirty,
                        handleChange,
                        handleSubmit,
                        setFieldValue,
                        values,
                    }) => {
                        return (
                            <Form>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    {/* User Role Details Form */}
                                    <Box
                                        sx={{
                                            ...DEFAULT_BORDER,
                                            backgroundColor: '#FFFFFF',
                                            boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                                            padding: '32px 24px',
                                        }}
                                        display="flex"
                                        flexDirection="column"
                                        gap="32px"
                                    >
                                        <Grid item xs={12} sx={{ paddingTop: '0px' }}>
                                            <Box>
                                                <Typography variant="h2">User Role</Typography>
                                                <Typography>
                                                    Provide the user role details below
                                                </Typography>
                                                <Divider
                                                    sx={{
                                                        borderColor: 'warning.main',
                                                        marginTop: '8px',
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            container
                                            rowSpacing={2}
                                            columnSpacing={2}
                                        >
                                            <Grid item xs={12} lg={6}>
                                                <CustomInput
                                                    name="name"
                                                    label="User Role Name"
                                                    placeholder="Enter User Role"
                                                    onChange={handleChange}
                                                    value={values.name}
                                                    error={errors.name}
                                                />
                                            </Grid>
                                            <Grid item lg={6} xs={12}>
                                                <DropDown
                                                    name="hierarchyLevel"
                                                    label="Hierarchy Level"
                                                    placeholder="Select Hierarchy Level"
                                                    onChange={(event) => {
                                                        handleHierarchyDropDownChange(event);
                                                    }}
                                                    options={HIERARCHIES.map((hierarchy) => {
                                                        return {
                                                            value: hierarchy.value,
                                                            label: hierarchy.label,
                                                        };
                                                    })}
                                                />
                                            </Grid>
                                        </Grid>

                                        {values.hierarchyLevel !== 'NPI' &&
                                            values.hierarchyLevel !== '' && (
                                                <Grid item xs={12}>
                                                    <Divider
                                                        sx={{
                                                            borderColor: '#F0F0F0',
                                                        }}
                                                    />
                                                </Grid>
                                            )}

                                        {/* Partner of Partner Dropdown */}
                                        {values.hierarchyLevel === Hierarchy.PARTNER_OF_PARTNER && (
                                            <Grid container spacing={2}>
                                                <Grid item lg={6} xs={12}>
                                                    <DropDown
                                                        name="partnerOfPartner"
                                                        label="Partner of Partner"
                                                        onChange={(event) => {
                                                            const selectedPoPId = event;
                                                            // Find the business data corresponding to the selected PoP ID
                                                            const businessData =
                                                                organisationData?.businesses.find(
                                                                    (business) => {
                                                                        return (
                                                                            business.id.toString() ===
                                                                            selectedPoPId
                                                                        );
                                                                    }
                                                                );

                                                            const partnerOfPartnerId = Number(
                                                                businessData?.partnerOfPartnerId
                                                            );

                                                            setPartnerOfPartnerId(
                                                                partnerOfPartnerId
                                                            );
                                                        }}
                                                        options={partnerOfPartnerDropDownOptions?.map(
                                                            (organisation) => {
                                                                return {
                                                                    value: organisation.id.toString(),
                                                                    label: organisation.legalName,
                                                                };
                                                            }
                                                        )}
                                                        placeholder="Select Partner of Partner"
                                                    />
                                                </Grid>
                                                <Grid item lg={6} xs={12}>
                                                    <CustomInput
                                                        name="partnerLegalName"
                                                        label="Partner"
                                                        placeholder="Please select a Partner Of Partner"
                                                        value={partnerLegalName}
                                                        disabled
                                                    />
                                                </Grid>
                                            </Grid>
                                        )}

                                        {/* Merchant Dropdown */}
                                        {values.hierarchyLevel === Hierarchy.MERCHANT && (
                                            <Grid container spacing={2}>
                                                <Grid item lg={6} xs={12}>
                                                    <DropDown
                                                        name="merchant"
                                                        label="Merchant"
                                                        onChange={(event) => {
                                                            const selectedMerchantId = event;

                                                            // Find the merchant data corresponding to the selected Merchant ID
                                                            const businessData =
                                                                organisationData?.businesses.find(
                                                                    (business) => {
                                                                        return (
                                                                            business.id.toString() ===
                                                                            selectedMerchantId
                                                                        );
                                                                    }
                                                                );

                                                            const merchantId =
                                                                businessData?.merchantId;
                                                            setMerchantId(merchantId);
                                                        }}
                                                        options={merchantDropDownOptions.map(
                                                            (organisation) => {
                                                                return {
                                                                    value: organisation.id.toString(),
                                                                    label: organisation.legalName,
                                                                };
                                                            }
                                                        )}
                                                        placeholder="Select Merchant"
                                                    />
                                                </Grid>
                                                <Grid item lg={6} xs={12}>
                                                    <CustomInput
                                                        name="selectedMerchant"
                                                        label="Partner"
                                                        placeholder="Please select a Merchant"
                                                        value={selectedMerchant}
                                                        disabled
                                                    />
                                                </Grid>
                                            </Grid>
                                        )}

                                        {values.hierarchyLevel === Hierarchy.PARTNER && (
                                            <Grid item lg={6} xs={12}>
                                                <DropDown
                                                    name="partner"
                                                    label="Partner"
                                                    options={
                                                        partnerDropDownOptions
                                                            ? partnerDropDownOptions?.map(
                                                                  (organisation) => {
                                                                      return {
                                                                          value: organisation.id.toString(),
                                                                          label: organisation.legalName,
                                                                      };
                                                                  }
                                                              )
                                                            : []
                                                    }
                                                    placeholder="Select Partner"
                                                />
                                            </Grid>
                                        )}
                                    </Box>

                                    {/* Partner Features. Hide unless if no hierarchy level is selected */}
                                    {values.hierarchyLevel !== '' && (
                                        <PartnerFeatures
                                            handlePermissionsChange={handlePermissionsChange}
                                            permissions={permissions}
                                        />
                                    )}

                                    {/* Submit Buttons */}
                                    <Grid
                                        item
                                        xs={12}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            gap: '8px',
                                        }}
                                    >
                                        <Button
                                            variant="outlined-black"
                                            sx={{ width: '146px' }}
                                            onClick={() => {
                                                return navigate('/user-management/user-roles');
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="main"
                                            sx={{ width: '146px' }}
                                            type="submit"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                // Check if the form is dirty or invalid before submitting
                                                if (!values.name || !values.hierarchyLevel) {
                                                    showErrorToast(
                                                        'Please fill in required fields'
                                                    );
                                                } else {
                                                    handleCreateNewUserRole(values);
                                                }
                                            }}
                                        >
                                            Save
                                        </Button>
                                    </Grid>
                                </Box>
                            </Form>
                        );
                    }}
                </Formik>
            </Grid>
        </PageContent>
    );
};

export default AddUserRolePage;
